<template>
  <div class="container">
    <HMModalHeader
      :title="$t(titleKey)"
      v-if="showAsModal"
      @close="$emit('close')"
    />
    <TopNav class="mt-4" v-else :showOpenMainNav="true">
      <NavItem
        v-if="isEditable"
        icon="far fa-pencil-alt"
        :text="$t('GENERAL.BUTTON.REPLACE_CONTENT')"
        :loading="inProgress"
        @click="editMediaFile"
      />
      <NavItem
        icon="far fa-save"
        :text="$t('GENERAL.BUTTON.SAVE')"
        :pulse="isDirty"
        :loading="inProgress"
        @click="submitMedia"
      />
    </TopNav>
    <div class="mt-4 text-center">
      <HMFilePreview
        v-if="formModel.media"
        :file="formModel.media"
        class="hm-media-file-preview d-inline-block"
        :checkIfVideoInProgress="true"
        :ignorePlaybackStartStop="true"
        @vimeoPlayerReady="onVimeoPlayerReady"
        @vimeoPlayerLoaded="onVimeoPlayerLoaded"
        @vimeoPlayerDurationChanged="onVimeoPlayerDurationChanged"
        @vimeoPlayerSeeked="onVimeoPlayerSeeked"
        @vimeoPlayerTimeupdate="onVimeoPlayerTimeupdate"
        :key="vimeoClipId"
      />
    </div>
    <ValidationObserver ref="form" v-slot="{ invalid }">
      <form @submit.prevent="submitMedia" class="media-form">
        <div class="row">
          <div
            class="col-12"
            v-if="formModel.media && formModel.media.file_type === 'recording'"
          >
            <h4 class="mt-4 mb-5">
              {{ $t('MEDIA_LIBRARY.TEXT.SET_PLAYBACK_START_END') }}
            </h4>
            <HMSlider
              v-model="playbackStartEndRange"
              :min="0"
              :max="formModel.media.video_length"
              :interval="1"
              :tooltip-formatter="playbackRangeFormatter"
              :enableCross="false"
            ></HMSlider>
          </div>
          <div
            class="col-12"
            v-if="
              formModel.media &&
              formModel.media.is_uploaded &&
              formModel.media.file_type === 'vimeo' &&
              vimeoPlayer
            "
          >
            <div class="text-center" v-if="!vimeoTrimVisible">
              <HMButton
                class="mt-3"
                @click="vimeoTrimVisible = true"
                color="outline-brand"
                size="sm"
              >
                {{ $t('MEDIA_LIBRARY.BUTTON.OPEN_TRIMMING_TOOL') }}
              </HMButton>
            </div>
            <div v-else>
              <h4 class="mt-4 mb-5">
                {{ $t('MEDIA_LIBRARY.TEXT.TRIM_VIDEO') }}
              </h4>
              <HMSlider
                v-model="vimeoTrimRange"
                :min="0"
                :max="vimeoVideoLength"
                :interval="0.1"
                :tooltip-formatter="playbackRangeFormatterForVimeo"
                :enableCross="false"
              ></HMSlider>
              <div
                class="d-flex justify-content-between align-items-center flex-nowrap"
              >
                <span
                  class="smaller pointer hover-coral"
                  @click="setVimeoCurrentPositionTrimStart"
                >
                  Set start of trim at current video position
                </span>
                <span
                  class="smaller pointer hover-coral"
                  @click="setVimeoCurrentPositionTrimEnd"
                >
                  Set end of trim at current video position
                </span>
              </div>
              <div class="text-center">
                <HMButton
                  color="outline-brand"
                  size="sm"
                  :loading="trimmingVimeoVideo"
                  :disabled="trimmingVimeoVideo || !isTrimmerSet"
                  v-confirm="{
                    title: $t('MEDIA_LIBRARY.TITLE.CONFIRM_VIDEO_TRIMMING'),
                    text: $t('MEDIA_LIBRARY.TEXT.CONFIRM_VIDEO_TRIMMING'),
                    confirmButton: $t('MEDIA_LIBRARY.BUTTON.TRIM_VIDEO'),
                    confirm: () => trimVimeoVideo(),
                  }"
                >
                  {{ $t('MEDIA_LIBRARY.BUTTON.TRIM_VIDEO') }}
                </HMButton>
              </div>
              <hr />
            </div>
          </div>
          <div class="col-12 mt-4">
            <label>{{ $t('MEDIA_LIBRARY.LABEL.THUMBNAIL') }}</label>
          </div>
          <div class="col-12 col-md-6">
            <HMImageUpload
              :updateThumbnail="true"
              :imageDescription="$t('MEDIA_LIBRARY.LABEL.THUMBNAIL')"
              v-model="formModel.media"
              :canvaWidth="1920"
              :canvaHeight="1080"
              inputName="media-library-photo"
            />
          </div>
        </div>
        <div class="row mt-4">
          <div class="col-12">
            <HMTextInput
              type="text"
              rules="required"
              :label="$t('MEDIA_LIBRARY.LABEL.NAME')"
              v-model="formModel.title"
              :name="$t('MEDIA_LIBRARY.LABEL.NAME')"
            />
          </div>
          <div class="col-md-6">
            <div class="form-group">
              <label>{{ $t('MEDIA_LIBRARY.LABEL.RELEASE_ON') }}</label>
              <b-form-datepicker
                v-model="releaseOnDate"
                :locale="$i18n.locale"
                :date-format-options="{
                  year: 'numeric',
                  month: 'short',
                  day: '2-digit',
                  weekday: 'short',
                }"
              />
            </div>
          </div>
          <div class="col-md-6">
            <div class="form-group">
              <label>{{ $t('MEDIA_LIBRARY.LABEL.AT') }}</label>
              <HMTimePicker v-model="releaseOnTime" />
            </div>
          </div>
          <div class="col-md-6">
            <div class="form-group">
              <label>{{ $t('MEDIA_LIBRARY.LABEL.INSTRUCTOR') }}</label>
              <v-select
                :options="instructors"
                label="name"
                code="id"
                v-model="formModel.instructor"
              />
            </div>
          </div>
          <div class="col-md-6">
            <div class="form-group">
              <label>{{ $t('MEDIA_LIBRARY.LABEL.TAGS') }}</label>
              <v-select
                :options="categoriesTags"
                multiple
                label="name"
                code="id"
                v-model="formModel.options"
              />
            </div>
          </div>
          <div class="col-12">
            <div class="form-group d-flex align-items-center">
              <label class="c-charcoal m-0 mr-4">{{
                $t('MEDIA_LIBRARY.LABEL.DISABLE_FAST_FORWARD')
              }}</label>
              <HMSwitch
                class="ml-3"
                v-model="formModel.prevent_forward_seeking"
              />
            </div>
          </div>
          <div class="col-12">
            <div class="form-group">
              <HMHTMLEditor
                class="mt-1"
                v-model="formModel.description_new"
                :htmlValue="formModel.description"
                :title="$t('MEDIA_LIBRARY.LABEL.DESCRIPTION')"
              />
            </div>
          </div>
          <div class="col-12 mt-4 mb-3">
            <div>
              <h3 class="mb-4">
                {{ $t('MEDIA_LIBRARY.TITLE.ATTACHMENTS') }}
              </h3>
              <div class="mb-3">
                {{ $t('MEDIA_LIBRARY.TEXT.ATTACHMENTS_DESCRIPTION') }}
              </div>
            </div>
            <div class="form-group">
              <HMSimpleActionCard
                class="mb-3"
                :prefixIcon="['fas', 'image']"
                v-for="(file, index) in formModel.attachments"
                :key="file.id"
                :title="cleanedFileName(file.file_name)"
                @click="downloadAttachment(file)"
              >
                <template slot="actions">
                  <font-awesome-icon
                    class="c-coral ml-2"
                    :icon="['far', 'trash']"
                    @click="removeFile(index)"
                  />
                </template>
              </HMSimpleActionCard>
              <HMFileUpload
                class="my-4"
                :value="null"
                @input="addFile"
                :title="$t('MEDIA_LIBRARY.TITLE.UPLOAD_ATTACHMENT')"
                :description="
                  $t('MEDIA_LIBRARY.TEXT.SUPPORTED_ATTACHMENTS_FORMATS')
                "
                inputName="attachment"
              />
            </div>
          </div>
        </div>
        <HMModalFooter
          v-if="showAsModal"
          :submitButton="$t('GENERAL.BUTTON.SAVE')"
          :disabled="invalid || inProgress"
          :formError="formError"
          @close="$emit('close')"
        />
      </form>
    </ValidationObserver>
  </div>
</template>
<script>
import { ValidationObserver } from 'vee-validate';
import { get, cloneDeep } from 'lodash';
import { mapGetters, mapState } from 'vuex';
import TopNav from '@/web/components/navigation/TopNav.vue';
import NavItem from '@/web/components/navigation/NavItem';
import editorToolbar from '@/web/utils/editor-toolbar';
import { typeInfo, getMediaInfoType } from '@/web/utils/media';
import DirtyMixin from '@/web/mixins/DirtyMixin';
import { scrollToFirstFormError } from '@/core/utils/errors';
import { cleanedFileName } from '@/core/utils/utils';
import HMTimePicker from '@/web/components/common/HMTimePicker';
import moment from 'moment';
import _ from 'lodash';
import HMButton from '@/web/components/common/HMButton';
import VimeoService from '@/web/services/vimeo-service';
import MediaFormModal from '@/web/components/media-library/MediaFormModal';
import TeacherPermissionsMixin from '@/web/mixins/TeacherPermissionsMixin';
import { deleteFile } from '@/web/services/upload';

export default {
  name: 'LibraryAddMedia',
  mixins: [DirtyMixin, TeacherPermissionsMixin],
  components: {
    HMButton,
    TopNav,
    NavItem,
    ValidationObserver,
    HMTimePicker,
  },
  props: {
    medaiData: {
      required: null,
      type: Object,
    },
    medaiFile: {
      required: null,
      type: Object,
    },
    showAsModal: {
      required: false,
      type: Boolean,
    },
  },
  data: () => ({
    embedFileUrl: null,
    formModel: {},
    orphanedFile: null, // file that gets overriden if content of the media is changed
    initialized: false,
    formError: null,
    inProgress: false,
    uploadType: 'upload-file',
    customToolbar: editorToolbar,
    media: {
      id: null,
      title: null,
      media: null,
      instructor: null,
      options: [],
      description: null,
      description_new: null,
    },
    vimeoPlayer: null,
    vimeoVideoLength: 0,
    vimeoTrimRange: [],
    currentVimeoPosition: 0,
    trimmingVimeoVideo: false,
    vimeoClipId: 1,
    vimeoTrimVisible: false,
    trimCheckInterval: null,
  }),
  computed: {
    ...mapState('mediaLibrary', ['instructors']),
    ...mapGetters('mediaLibrary', ['categoriesTags']),
    thumbnailStyle() {
      const thumbnail = this.formModel?.media?.thumbnail;
      return thumbnail ? { backgroundImage: `url(${thumbnail})` } : {};
    },
    infoTitle() {
      const fileContent = this.formModel?.media?.file_content || '';
      return `${fileContent} data`;
    },
    playbackStartEndRange: {
      get() {
        return [
          this.formModel?.media?.playback_start || 0,
          this.formModel?.media?.playback_end ||
            this.formModel?.media?.video_length,
        ];
      },
      set(range) {
        this.formModel.media.playback_start = range[0];
        this.formModel.media.playback_end = range[1];
      },
    },
    releaseOnDate: {
      get() {
        return moment(this.formModel.release_on).format('YYYY-MM-DD');
      },
      set(value) {
        const selectedDate = moment(value);
        const selectedStartTime = moment(this.formModel.release_on);
        selectedDate.hour(selectedStartTime.hour());
        selectedDate.minute(selectedStartTime.minute());
        this.formModel.release_on = _.clone(selectedDate.toISOString());
      },
    },
    releaseOnTime: {
      get() {
        return moment(this.formModel.release_on).format('HH:mm');
      },
      set(value) {
        const selectedDate = moment(this.formModel.release_on);
        const selectedStartTime = moment(value, ['HH:mm']);
        selectedDate.hour(selectedStartTime.hour());
        selectedDate.minute(selectedStartTime.minute());
        this.formModel.release_on = _.clone(selectedDate.toISOString());
      },
    },
    info() {
      if (!this.media.media) return;

      return typeInfo[getMediaInfoType(this.media.media)];
    },
    isEditable() {
      return this.media.id;
    },
    titleKey() {
      return this.media.id
        ? 'MEDIA_LIBRARY.TITLE.EDIT_FILE'
        : 'MEDIA_LIBRARY.TITLE.ADD_FILE';
    },
    getVimeoVideoId() {
      return this.media.media?.video_url?.split('/').pop();
    },
    isTrimmerSet() {
      if (!this.vimeoTrimRange.length) return false;
      const trimStart = this.vimeoTrimRange[0];
      const trimEnd = this.vimeoTrimRange[1];

      return trimStart > 0 || trimEnd < this.vimeoVideoLength;
    },
    canPreventFastForward() {
      return (
        ['recording', 'vimeo'].indexOf(this.formModel.media.file_type) > -1
      );
    },
  },
  methods: {
    async onVimeoPlayerReady(player) {
      console.log('onVimeoPlayerReady', player);
      this.vimeoPlayer = player;
    },
    async onVimeoPlayerLoaded({ data, player }) {
      console.log('onVimeoPlayerLoaded data', data);
      console.log('onVimeoPlayerLoaded player', player);
      this.vimeoVideoLength = await player.getDuration();
      this.vimeoTrimRange = [0, this.vimeoVideoLength];

      this.runTrimmingCheck(true);
    },
    async onVimeoPlayerDurationChanged({ data, player }) {
      console.log('onVimeoPlayerDurationChanged data', data);
      console.log('onVimeoPlayerDurationChanged player', player);
    },
    async onVimeoPlayerSeeked({ data, player }) {
      console.log('onVimeoPlayerSeeked', data, player);
    },
    async onVimeoPlayerTimeupdate({ data, player }) {
      console.log('onVimeoPlayerTimeupdate', data, player);
      this.currentVimeoPosition = data.seconds;
    },
    async setVimeoCurrentPositionTrimStart() {
      this.vimeoTrimRange = [this.currentVimeoPosition, this.vimeoTrimRange[1]];
    },
    async setVimeoCurrentPositionTrimEnd() {
      this.vimeoTrimRange = [this.vimeoTrimRange[0], this.currentVimeoPosition];
    },
    async trimVimeoVideo() {
      this.trimmingVimeoVideo = true;
      try {
        const duration = this.vimeoTrimRange[1] - this.vimeoTrimRange[0];
        await this.$store.dispatch('mediaLibrary/trimVideo', {
          id: this.media.media.id,
          trimStart: this.vimeoTrimRange[0],
          trimEnd: this.vimeoTrimRange[1],
        });
        // const result = await VimeoService.trimVideo(
        //   this.getVimeoVideoId,
        //   this.vimeoTrimRange[0],
        //   this.vimeoTrimRange[1]
        // );
        // await this.$store.dispatch('mediaLibrary/updateMediaFile', {
        //   id: this.media.media.id,
        //   data: { video_length: Math.round(duration) },
        // });
        this.$notify({
          type: 'success',
          title: this.$t('NOTIFICATIONS.TITLE.SUCCESS'),
          text: this.$t('MEDIA_LIBRARY.NOTIFICATIONS.VIDEO_TRIM_STARTED'),
        });
      } catch (error) {
        console.log(error);
        this.$notify({
          type: 'error',
          title: 'Error',
          text: error.message,
        });
        this.trimmingVimeoVideo = false;
        return;
      }
      this.runTrimmingCheck();
    },
    runTrimmingCheck(isInitial) {
      if (isInitial && !this.media.media.vimeo_storyboard_id) {
        return;
      }
      this.trimCheckInterval = setInterval(
        async () => {
          try {
            const result = await this.$store.dispatch(
              'mediaLibrary/checkTrimProgress',
              this.media.media.id
            );
            console.log('getTrimVideoStatus result =', result);
            if (result?.status === 'ERR') {
              this.trimmingVimeoVideo = false;
              clearInterval(this.trimCheckInterval);
            }
            if (result?.session_status === 'DRFT') {
              this.trimmingVimeoVideo = false;
              console.log('isInitial', isInitial);
              if (!isInitial) {
                this.vimeoClipId += 1;
                console.log('increased vimeoClipId=', this.vimeoClipId);
                this.$notify({
                  type: 'success',
                  title: this.$t('NOTIFICATIONS.TITLE.SUCCESS'),
                  text: this.$t(
                    'MEDIA_LIBRARY.NOTIFICATIONS.VIDEO_TRIM_COMPLETED'
                  ),
                });
              }
              clearInterval(this.trimCheckInterval);
            } else {
              this.trimmingVimeoVideo = true;
            }
          } catch (error) {
            console.log(error);
            this.trimmingVimeoVideo = false;
            clearInterval(this.trimCheckInterval);
          }
        },
        5000,
        3000
      );
    },
    playbackRangeFormatter(seconds) {
      return new Date(seconds ? seconds * 1000 : null)
        .toISOString()
        .substr(11, 8);
    },
    playbackRangeFormatterForVimeo(seconds) {
      return new Date(seconds ? seconds * 1000 : null)
        .toISOString()
        .substr(11, 10);
    },
    formModelToPostData(formModel) {
      const data = { ...formModel };
      data.title = formModel.title;
      data.media = formModel.media.id;
      data.instructor = formModel.instructor?.id || null;
      data.options = formModel.options.map(option => option.id);
      data.attachments = formModel.attachments.map(attachment => attachment.id);
      return data;
    },
    mediaModelToFormModel(media) {
      const formModel = cloneDeep(media);
      formModel.options = formModel.options?.map(
        ({ option_id, category_name, option_name }) => ({
          id: option_id,
          name: `${category_name} - ${option_name}`,
        })
      );
      formModel.attachments = formModel.attachments || [];
      return formModel;
    },
    async editMediaFile() {
      this.$modal.show(
        MediaFormModal,
        {
          showSelectMedia: false,
          showEditMedia: false,
          showUploadMedia: true,
          showEmbedMedia: true,
          showEmbedHtml: true,
          videoUploadAllowed: this.videoUploadAllowed,
          justFile: true,
          existingContent: this.media?.media?.content,
          existingContentRaw: this.media?.media?.content_raw,
        },
        { clickToClose: false },
        {
          'file-added': file => {
            this.orphanedFile = this.formModel.media;
            this.formModel.media = file;
            this.submitMedia();
          },
        }
      );
    },
    async submitMedia() {
      if (this.inProgress) {
        return;
      }
      this.$refs.form.validate();
      if (this.$refs.form.flags.invalid) {
        scrollToFirstFormError();
        return;
      }
      this.inProgress = true;
      const dispatchAction = this.media.id
        ? 'mediaLibrary/updateMedia'
        : 'mediaLibrary/createMedia';

      const data = this.formModelToPostData(this.formModel);

      try {
        if (this.media.id) {
          await this.$store.dispatch('mediaLibrary/updatePlayback', {
            id: this.media?.media?.id,
            playback_start: this.formModel?.media?.playback_start,
            playback_end: this.formModel?.media?.playback_end,
          });
        }
        const dispatchPromise = await this.$store.dispatch(
          dispatchAction,
          data
        );
        if (this.orphanedFile) {
          await deleteFile(this.orphanedFile.id);
          this.orphanedFile = null;
        }
        this.clearDirty();
        if (this.showAsModal) {
          this.$emit('close');
          this.$parent.$emit('media-saved', dispatchPromise);
        } else {
          this.$router.replace({
            name: 'media-library-view-item',
            params: { media_id: dispatchPromise.id },
          });
        }
        this.$emit('onSubmit', dispatchPromise);
      } catch (error) {
        this.formError = get(error, 'response.data.non_field_errors[0]', null);
      }
      this.inProgress = false;
    },
    addFile(file) {
      this.formModel.attachments.push(file);
    },
    removeFile(fileIndex) {
      this.formModel.attachments.splice(fileIndex, 1);
    },
    downloadAttachment(file) {
      return window.open(file.storage_url, '_blank');
    },
    cleanedFileName,
  },
  async mounted() {
    if (this.medaiData) {
      this.media = this.medaiData;
    } else if (this.medaiFile) {
      this.media.media = this.medaiFile;
    } else if (this.$route.params.media) {
      // media-library-add route
      this.media.media = this.$route.params.media;
    } else {
      // media-library-edit-item route
      this.media = await this.$store.dispatch(
        'mediaLibrary/getMediaById',
        this.$route.params.media_id
      );
    }
    this.formModel = this.mediaModelToFormModel(this.media);
    this.$store.dispatch('mediaLibrary/getInstructors');
    this.$store.dispatch('mediaLibrary/getCategories');
    setTimeout(() => {
      this.initialized = true;
    }, 200);
  },
  destroyed() {
    if (this.trimCheckInterval) {
      clearInterval(this.trimCheckInterval);
    }
  },
  watch: {
    formModel: {
      handler: function (formModel, oldFormModel) {
        if (oldFormModel && this.initialized) {
          this.setDirty();
        }
      },
      deep: true,
    },
    vimeoTrimRange: {
      handler: async function (vimeoTrimRange, oldVimeoTrimRange) {
        if (!this.vimeoPlayer) return;
        if (!vimeoTrimRange.length) return;
        if (!oldVimeoTrimRange.length) return;

        if (vimeoTrimRange[0] !== oldVimeoTrimRange[0]) {
          await this.vimeoPlayer.setCurrentTime(vimeoTrimRange[0]);
          await this.vimeoPlayer.play();
          await this.vimeoPlayer.pause();
        }
        if (vimeoTrimRange[1] !== oldVimeoTrimRange[1]) {
          await this.vimeoPlayer.setCurrentTime(vimeoTrimRange[1]);
          await this.vimeoPlayer.play();
          await this.vimeoPlayer.pause();
        }
      },
      deep: true,
    },
  },
};
</script>

<style lang="scss" scoped>
@import '@/web/styles/components-variables.scss';

.thumbnail-image {
  max-width: 200px;
}
.hm-media-file-preview ::v-deep .embed-responsive-3by4 {
  max-height: 600px !important;
}
</style>
